/**
 * A person's `translations` map (`PersonTranslationDbDto`: `{ name }` per locale) as the edit
 * form's rows, and back.
 *
 * `UpdatePersonCommand.Translations` replaces the WHOLE map, so the form always sends every row
 * it holds: a row the curator removed is a locale removed. Blank rows are dropped, locale codes
 * are lowercased, and the last row wins when two share a code.
 */
export interface PersonTranslationRow {
  locale: string;
  name: string;
}

export type PersonTranslationsMap = Record<string, { name?: string | null }>;

export function toTranslationRows(raw: unknown): PersonTranslationRow[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>)
    .map(([locale, value]) => ({
      locale,
      name:
        value && typeof value === "object" && "name" in value
          ? String((value as { name?: unknown }).name ?? "")
          : "",
    }))
    .filter((row) => row.name.trim().length > 0)
    .sort((a, b) => a.locale.localeCompare(b.locale));
}

export function toTranslationsMap(
  rows: ReadonlyArray<Partial<PersonTranslationRow>> | null | undefined,
): PersonTranslationsMap {
  const map: PersonTranslationsMap = {};
  for (const row of rows ?? []) {
    const locale = (row.locale ?? "").trim().toLowerCase();
    const name = (row.name ?? "").trim();
    if (locale && name) map[locale] = { name };
  }
  return map;
}
